import Model, { encoders, ID, JsonArray, MODEL_LEVEL } from 'src/models/model'
import Librariable from 'src/models/model/concerns/Librariable'
import Movie from 'src/models/movie/Movie'
import MovieTermWeek from 'src/models/movie/MovieTermWeek'
import ShowingBadge from 'src/models/showing/ShowingBadge'

const ACTIONS = {
  SETTLE: 'settle',
  UNSETTLE: 'unsettle'
}
ACTIONS.ALL = _.values(ACTIONS)

export default class MovieTerm extends Model {
  static name = 'MovieTerm'

  static paginate = true

  static filterable = true

  static deletable = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.StudioReporting]

  static define () {
    this.concerns = [Librariable]

    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      abbreviation: {
        type: String,
        computed () {
          if (this.isRemote) return this.data.abbreviation

          return _.toUpper(this.format.replace(/\W/g, '').substring(0, 5), 5)
        }
      },
      movie: {
        type: Movie,
        default: null,
        encode: encoders.hasOne,
        output: true
      },
      showingBadges: {
        type: [ShowingBadge],
        default: () => ([]),
        encode (data) {
          data.showingBadgeIds = _.map(this.showingBadges.data, 'id')
        },
        reference: true
      },
      hasFlatFee: {
        type: Boolean,
        default: false
      },
      flatFee: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      minimumGuarantee: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      deposit: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      previewTerms: {
        type: JsonArray,
        mergeDefault: true,
        default: () => ([])
      },
      weeklyTerms: {
        type: JsonArray,
        mergeDefault: true,
        default: () => ([0])
      },
      movieTermWeeks: {
        type: [MovieTermWeek],
        default: () => ([]),
        acceptsNestedAttributes: true,
        allowDestroy: true
      },
      priorityOrder: {
        type: Number,
        default: 100
      },
      notes: {
        type: String,
        default: null,
        excludeFromDefault: true
      },
      showingCount: {
        type: Number,
        default: null,
        excludeFromDefault: true,
        output: true
      },
      locked: {
        type: Boolean,
        default: false,
        excludeFromDefault: true,
        output: true
      },
      action: {
        type: String,
        default: null,
        input: true
      }
    }

    this.computed = {
      name () {
        if (this.showingBadges?.data?.length > 0) {
          return `${this.movie?.name} (${_.map(this.showingBadges?.data, 'displayName').join(', ')})`
        } else {
          return this.movie?.name
        }
      },
      format () {
        if (this.showingBadges?.data?.length > 0) {
          return `${_.map(this.showingBadges.data, 'displayName').join(', ')}`
        } else {
          return 'Default'
        }
      },
      validityIcon () {
        if (_.isNil(this.blockingMovieTerm)) {
          return {
            name: 'check',
            color: 'positive',
            tooltip: this.validityTooltip
          }
        }

        return {
          name: 'error',
          color: 'negative',
          tooltip: this.validityTooltip
        }
      },
      blockingMovieTerm () {
        const blockingMovieTerm = _.find(_.sortBy(this.movie?.movieTerms?.data, 'priorityOrder'), otherMovieTerm => {
          if (otherMovieTerm.id === this.id) return true

          const matchingShowingBadges = _.intersection(otherMovieTerm.showingBadgeIds, this.showingBadgeIds)

          // If the other movie term only has showing badges that match my showing badges,
          // Then it's showing badges are a subset of my own.
          // This means that this movie term is unreachable.
          return matchingShowingBadges.length === otherMovieTerm.showingBadgeIds.length
        })

        // If the blocking movie term is myself, I am not blocked
        // And should not return a blocking movie term
        if (blockingMovieTerm?.id !== this.id) {
          return blockingMovieTerm
        }

        return null
      },
      validityTooltip () {
        if (!_.isNil(this.blockingMovieTerm)) {
          let tooltip = `This movie term will never be assigned.
            Priority ${this.blockingMovieTerm.priorityOrder}`

          if (_.isEmpty(this.blockingMovieTerm.showingBadges?.data)) {
            tooltip += ' requires no showing badges and will match first for all showings.'
          } else {
            tooltip += ` will match first for all showings with showing badges ${this.blockingMovieTerm.format}.`
          }

          return tooltip
        }

        const excludedShowingBadgeCombinations = _.map(this.movie?.movieTerms?.data?.slice(0, this.priorityOrder - 1), movieTerm => {
          return _.map(movieTerm.showingBadges?.data, 'displayName').join(' & ')
        }).join(', or ')

        if (this.showingBadges?.data?.length === 0) {
          return `This movie term applies to all showings${excludedShowingBadgeCombinations ? ` without ${excludedShowingBadgeCombinations}.` : '.'}`
        }

        const includedShowingBadgeCombination = _.map(this.showingBadges?.data, 'displayName').join(' & ')

        const includedText = !_.isEmpty(includedShowingBadgeCombination) ? ` with badges ${includedShowingBadgeCombination}` : null
        const excludedText = !_.isEmpty(excludedShowingBadgeCombinations) ? ` without ${excludedShowingBadgeCombinations}` : null

        return `Applies to ${_.isEmpty(includedText) ? 'all ' : ''}showings${_.compact([includedText, excludedText]).join(',')}.`
      },
      numberOfShowingBadges () {
        return this.showingBadges?.data?.length || 0
      },
      numberOfPriorityShowingBadges () {
        return _.countBy(this.showingBadges?.data, 'conferPriority').true || 0
      },
      totalShowingBadgeDisplayOrder () {
        return _.sumBy(this.showingBadges?.data, 'displayOrder')
      },
      sortedMovieTermWeeks () {
        return _.orderBy(this.movieTermWeeks?.data, [
          term => term.type === 'preview' ? 0 : 1,
          'weekNumber'
        ], ['asc', 'asc'])
      },
      releaseMovieTermWeeks () {
        return _.filter(this.sortedMovieTermWeeks, { type: 'release' })
      },
      previewMovieTermWeeks () {
        return _.filter(this.sortedMovieTermWeeks, { type: 'preview' })
      }
    }
  }

  get description () {
    return this.format
  }

  get isDefault () {
    return _.isEmpty(this.showingBadgeIds)
  }

  get deletable () {
    return this.isLocal || !this.isDefault
  }
}
